import { inject, observer } from 'mobx-react';
import React from 'react';
import MainSectionLayout from '../layout/MainSectionLayout';
import { Instance } from 'mobx-state-tree';
import { DutyExchangeStore } from '../../store/DutyExchangeStore';
import { ActionCode, Page, TLCode } from '../../common/enums';
import { ApplicationStore, AuthenticationStore, PharmacyStore } from '../../store';
import { RouteComponentProps } from 'react-router-dom';
import styled from '@emotion/styled';
import { css } from 'emotion';
import { DutyTypeStore } from '../../store/DutyTypeStore';
import { Card, Tab, Tabs } from '@blueprintjs/core';
import SimpleFusionTab from './SimpleFusionTab';
import SimpleTransferTab from './SimpleTransferTab';
import TransferWithFusionTab from './TransferWithFusionTab';
import RefDataTab from './RefDataTab';
import RequestTab from './RequestTab';
import { PharmacyDetails } from '../../store/models/PharmacyDetails';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { DutyAssignment } from '../../store/models/DutyAssignment';
import { SystemStatusStore } from '../../store/SystemStatusStore';
import { SystemStatusSubSystem } from '../../common/enums/SystemStatusSubSystem';
import { SystemStatus } from '../../store/models/SystemStatus';
import { ModalStore } from '../../store/ModalStore';
import { DutyExchangeItemStore } from '../../store/DutyExchangeItemStore';
import { DutyExchangeItem } from '../../store/models/DutyExchangeItem';

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const CardStyle = css({
  height: '100%',
  '& .bp3-tabs': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  '& .bp3-tab-panel': {
    flex: 1,
  },
});

interface TransferPageProps extends RouteComponentProps<{ pharmacyId: string }>, WithTranslateProps {
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  DutyExchangeStore?: Instance<typeof DutyExchangeStore>;
  DutyExchangeItemStore?: Instance<typeof DutyExchangeItemStore>;
  DutyTypeStore?: Instance<typeof DutyTypeStore>;
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  ModalStore?: Instance<typeof ModalStore>;
}

interface TransferPageState {
  isLoading: boolean;
  exchangeIsSaving: boolean;
  pharmacyExchangeRequesterSelected?: Instance<typeof PharmacyDetails>;
  pharmacyExchangeNeighborSelected?: Instance<typeof PharmacyDetails>;
  withAgreement?: boolean;
  withoutSubstitution?: boolean;
  comment?: string;
  pharmacyNeighboringList?: Array<Instance<typeof PharmacyDetails>>;
  duties?: Array<Instance<typeof DutyAssignment>>;
  neighborDuties?: Array<Instance<typeof DutyAssignment>>;
  requesterDutiesSelected: number[];
  neighborDutiesSelected: number[];
  pharmacyUser: boolean;
  pharmacyId?: number;
  systemStatus?: Array<Instance<typeof SystemStatus>>;
  exchangeItemHistory: Array<Instance<typeof DutyExchangeItem>>;
  onlyFutureDutyExchange?: boolean;
}

@inject('ModalStore')
@inject('AuthenticationStore')
@inject('ApplicationStore')
@inject('PharmacyStore')
@inject('DutyExchangeStore')
@inject('DutyExchangeItemStore')
@inject('DutyTypeStore')
@inject('SystemStatusStore')
@observer
class TransferPage extends React.Component<TransferPageProps, TransferPageState> {
  state = {
    isLoading: false,
    exchangeIsSaving: false,
    duties: [],
    neighborDuties: [],
    requesterDutiesSelected: [],
    neighborDutiesSelected: [],
    pharmacyUser: false,
    withAgreement: false,
    withoutSubstitution: false,
    comment: '',
    exchangeItemHistory: [],
    onlyFutureDutyExchange: true,
  } as TransferPageState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    this._isMounted && this.setState({ isLoading: true });
    const { PharmacyStore, AuthenticationStore, ApplicationStore, translate } = this.props;
    PharmacyStore!.loadAll();
    const userRoles = AuthenticationStore!.userActionCodes;

    const criteria: any = {
      subSystem: 'lastPublishedDuty',
    };
    const systemStatus = await this.props.SystemStatusStore!.search(criteria);
    // @ts-ignore
    this._isMounted && this.setState({ systemStatus });

    if (userRoles.includes(ActionCode.PHARMACY_USER_CODE)) {
      if (AuthenticationStore!.pharmacyId === null) {
        ApplicationStore!.navigate(`${Page.Error}/${translate(TLCode.ERROR_NoPharmacyRelatedToThisUser)}`);
      }
      // this.onExchangeRequesterSelected(PharmacyStore!.getById(AuthenticationStore!.pharmacyId!)!);
      this._isMounted && this.setState({ pharmacyUser: true });

      const criteriaBP: any = {
        pharmacyId: AuthenticationStore!.pharmacyId,
        subSystem: SystemStatusSubSystem.EXCHANGE,
      };
      const systemStatus = await this.props.SystemStatusStore!.search(criteriaBP);
      // @ts-ignore
      this._isMounted && this.setState({ systemStatus });
    }

    this._isMounted && this.setState({ isLoading: false });
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.props.DutyExchangeItemStore!.clearData();
  }

  render() {
    const { PharmacyStore, translate } = this.props;

    //TODO : Rename "processNewCoordinates" ? or "postNewCoordinates"
    const onSave = () => {
      //TODO
    };

    const pharmacyList = PharmacyStore!.dataList;
    return (
      <MainSectionLayout isLoading={this.state.isLoading}>
        <Container>
          <Card className={CardStyle.toString()}>
            <Tabs
              id="main"
              defaultSelectedTabId="simpleTransfer"
              renderActiveTabPanelOnly={true}
              onChange={async (newTabId) => {
                if (newTabId === 'history') {
                  // await this.ensureHistoryLoaded();
                }
              }}
            >
              <Tab
                id="simpleTransfer"
                title={translate(TLCode.TRANSFER_SimpleTransferTabTitle)}
                panel={
                  <SimpleTransferTab
                    pharmacyList={pharmacyList}
                    getPharmacyById={PharmacyStore!.getById}
                    onSave={onSave}
                  />
                }
              />
              <Tab
                id="transferWithFusion"
                title={translate(TLCode.TRANSFER_TransferWithFusionTabTitle)}
                panel={
                  <TransferWithFusionTab
                    pharmacyList={pharmacyList}
                    getPharmacyById={PharmacyStore!.getById}
                    onSave={onSave}
                  />
                }
              />
              <Tab
                id="simpleFusion"
                title={translate(TLCode.TRANSFER_SimpleFusionTabTitle)}
                panel={<SimpleFusionTab pharmacyList={pharmacyList} onSave={onSave} />}
              />
              <Tab id="refData" title={translate(TLCode.TRANSFER_RefDataTabTitle)} panel={<RefDataTab />} />
              <Tab id="requests" title={translate(TLCode.TRANSFER_RequestTabTitle)} panel={<RequestTab />} />
              {/* <Tab id="protections" title={translate(TLCode.TRANSFER_ProtectionTabTitle)} panel={<ProtectionTab />} /> */}
            </Tabs>
          </Card>
        </Container>
      </MainSectionLayout>
    );
  }
}

export default withTranslate(TransferPage);
